import { ifDef } from "src/common/ifDef";
import { classSystemKeys } from "src/common/systemKey";
import { ZMfNode, ZMfNodeTabs } from "src/components/MForm/ZMfNode";
import { ZId, ZSidLabel } from "src/types";

import { ZAttrMeta } from "../attribute";
import { sortedAttributes } from "../attribute/sortedAttributes";
import { attrsRecordRawToInternal } from "../attribute/values/attrValueRawToInternal";
import { getSpecTitle, ZSpecCommon } from "../specification";
import { createSpecDefaultValues } from "../specification/createSpecDefaultValues";
import { ZSecSpecRow } from "../specification/ZSecSpecRow";
import { makeClassParentsOptions } from "./labels";
import { ZClassUnsafe } from "./ZClassRow";
import { ZClassSysData } from "./ZClassSysData";

/**
 * функция для извлечения вторичных спецификаций из данных формы класса
 */
export const getSecSpecsFromValues = (formData: unknown) => {
  const safeVal: { [classSystemKeys.secondarySpecs]?: ZSecSpecRow[] } =
    typeof formData === "object" && formData !== null
      ? formData
      : { [classSystemKeys.secondarySpecs]: [] };
  return safeVal[classSystemKeys.secondarySpecs] || [];
};

type ClassCardData = {
  data: unknown;
  meta: ZMfNode;
};

/**
 * Генерация данных и метаданных для карточки класса
 * Пока что костыльная реализация, т.к. нет никаких решений по управлению внешним видом формы
 * Системные данные выносятся в блок "@sys"
 * @param srcData
 * @param schemes
 * @returns
 */
export const makeClassInternalData = (
  srcData: ZClassUnsafe,
  schemes: ZSpecCommon[],
  parentClassId?: ZId,
  initialTopTab?: string,
): ClassCardData => {
  const mainMeta = {
    type: "vertNav",
    useNavBar: true,
    nodes: [] as ZMfNode[],
  } satisfies ZMfNode;

  const disabledAttrIds = new Set(srcData?.readonlyAttributes);

  const isNew = !srcData.id;

  // Генерация системных данных
  const { sysData, sysMeta } = makeSys(srcData, parentClassId);
  const data: Record<string, unknown> = sysData;
  mainMeta.nodes.push(sysMeta[classSystemKeys.first]);
  mainMeta.nodes.push(sysMeta[classSystemKeys.secondParents]);

  // Генерация блоков для спецификаций
  // MDM2-479 (Вторичные спецификации с типом Трансляция не должны появляться на карточке класса)
  schemes
    .filter(({ linkType }) => linkType?.code !== 1)
    .forEach((scheme) => {
      const attributes = sortedAttributes(scheme.definition?.attributes);
      const blockMeta = {
        type: "group",
        name: scheme.name,
        title: getSpecTitle(scheme),
        // extraLink: makeUrl(PageUrl.)  Пока не понятно, как отличать первичную от вторичной
        content: {
          node: {
            type: "grid",
            nodes: attributes.map((attr) => ({
              type: "attr",
              attr,
              disableIf: disabledAttrIds.has(attr.uid)
                ? { type: "true" }
                : undefined,
            })),
          },
        },
      } satisfies ZMfNode;
      mainMeta.nodes.push(blockMeta);

      // TODO: Сейчас реализован простой случай в том виде, как указано в задаче MDM2-932.
      // То есть, дефолтные значения применяются только в случае создания нового экземпляра.
      // Но тут может всё оказаться сложнее, если нужно будет учитывать новые спецификации, которые подключаются к существующим экземплярам.
      data[scheme.name] = isNew
        ? createSpecDefaultValues(scheme)
        : attrsRecordRawToInternal(srcData.data[scheme.name] ?? {}, attributes);
    });

  mainMeta.nodes.push(sysMeta[classSystemKeys.last]);

  const meta: ZMfNodeTabs = {
    type: "tabs",
    verticalFit: true,
    defaultTab: initialTopTab,
    tabs: [
      {
        key: "main",
        label: "Карточка",
        node: mainMeta,
      },
    ],
  };
  if (srcData.permissions?.canUpdate === false) {
    meta.disableIf = { type: "true" };
  }

  if (srcData.id) {
    // Пока что вкладка Спецификаций создаётся только при наличии srcData.id
    // Потому что при попытке добавить вторичную спецификацию сейчас вызывается запрос
    // /api/core/hierarchies/{classId}/schemas/secondary/available, в который
    // необходимо передать id класса. Иначе он даёт ошибку 404.
    meta.tabs.push({
      key: "specs",
      label: "Спецификации",
      node: {
        type: "grid",
        columnsCount: 1,
        nodes: [
          {
            type: "attr",
            attr: {
              name: classSystemKeys.primarySpec,
              title: "",
              uid: "",
              displayId: 0,
              type: { name: "strOption" },
              edit: {
                component: { type: "PrimarySpec" },
              },
            },
            disableIf: { type: "true" },
          },
          {
            type: "grid",
            columnsCount: 1,
            nodes: [
              {
                type: "attr",
                attr: {
                  name: classSystemKeys.secondarySpecs,
                  title: "",
                  type: {
                    name: "array",
                    item: {
                      type: { name: "strOption" },
                    },
                  },
                  uid: "",
                  displayId: 0,
                  edit: {
                    component: {
                      type: "SpecsSelectTable",
                      classId: srcData.id,
                      prohibitedNames: new Set(schemes.map(({ name }) => name)),
                    },
                  },
                },
              },
            ],
          },
        ],
      },
    });
  }

  return { data, meta };
};

// Костыльная генерация блока для системных атрибутов
const makeSys = (srcData: ZClassUnsafe, parentId?: ZId) => {
  /**
   * берем последние элементы в цепочках родителей и получаем массив ID
   */
  const parentsValues =
    srcData.parents
      ?.map((parent) => parent[parent.length - 1])
      .filter((parent) => parent !== undefined)
      .map((parent) => parent.id) || [];
  const uniqueValues = Array.from(
    new Map(parentsValues.map((val) => [val, val])).values(),
  );

  const metaMap: Record<
    | keyof Pick<
        ZClassUnsafe,
        | "createdAt"
        | "createdBy"
        | "displayId"
        | "id"
        | "updatedAt"
        | "updatedBy"
      >
    | "parentIds",
    Omit<ZAttrMeta, "displayId" | "name" | "uid">
  > = {
    displayId: {
      type: { name: "number" },
      title: "Display ID",
    },
    id: {
      type: { name: "string" },
      title: "UUID",
      edit: { colSpan: 2 },
    },
    createdBy: {
      type: { name: "strOption" },
      title: "Автор создания",
      edit: {
        component: {
          type: "Select",
          options: {
            type: "inline",
            list: optListFromValue(srcData.createdBy),
          },
        },
      },
    },
    createdAt: {
      type: { name: "dateTime" },
      title: "Дата создания",
    },
    updatedBy: {
      type: { name: "strOption" },
      title: "Автор последнего изменения",
      edit: {
        component: {
          type: "Select",
          options: {
            type: "inline",
            list: optListFromValue(srcData.updatedBy),
          },
        },
      },
    },
    updatedAt: {
      type: { name: "dateTime" },
      title: "Дата последнего изменения",
    },
    parentIds: {
      type: {
        name: "array",
        item: {
          type: { name: "strOption" },
        },
      },
      title: "Родитель",
      edit: {
        colSpan: "*",
        component: {
          type: "ParentsSelect",
          classId: srcData.id,
          parentId,
          placeholder: "Выбрать",
          drawerTitle: "Выбор родителя",
          options: {
            type: "inline",
            list: makeClassParentsOptions(srcData.parents || []),
          },
        },
      },
    },
  };

  const sysData = {
    [classSystemKeys.first]: {
      id: srcData.id || "new",
      displayId: srcData.displayId ?? -1,
    },
    [classSystemKeys.secondParents]: {
      parentIds: uniqueValues,
    },
    [classSystemKeys.primarySpec]: ifDef(
      srcData.primarySchema,
      ({ id, name, displayName, displayId, description }) => ({
        id,
        name,
        displayName,
        displayId,
        description,
      }),
    ),
    [classSystemKeys.secondarySpecs]: srcData.secondarySchemas
      ? [...srcData.secondarySchemas]
      : [],
    [classSystemKeys.last]: {
      createdBy: srcData.createdBy,
      createdAt: srcData.createdAt,
      updatedBy: srcData.updatedBy,
      updatedAt: srcData.updatedAt,
    },
  } satisfies ZClassSysData;

  const canEditAttr = (name: keyof typeof metaMap) => name === "parentIds";

  const createPart = (
    partKey: keyof ZClassSysData,
    fields: (keyof typeof metaMap)[],
    title: string,
  ) =>
    ({
      type: "group",
      name: partKey,
      title,
      content: {
        node: {
          type: "grid",
          nodes: fields.map((name) =>
            attrMeta(
              {
                name: String(name),
                ...metaMap[name],
              },
              canEditAttr(name),
            ),
          ),
        },
      },
    }) satisfies ZMfNode;

  return {
    sysData,
    sysMeta: {
      [classSystemKeys.first]: createPart(
        classSystemKeys.first,
        ["displayId", "id"],
        "Идентификаторы",
      ),
      [classSystemKeys.secondParents]: createPart(
        classSystemKeys.secondParents,
        ["parentIds"],
        "Иерархия",
      ),
      [classSystemKeys.last]: createPart(
        classSystemKeys.last,
        ["createdBy", "createdAt", "updatedBy", "updatedAt"],
        "Историчность",
      ),
    } satisfies Record<
      | typeof classSystemKeys.first
      | typeof classSystemKeys.last
      | typeof classSystemKeys.secondParents,
      ZMfNode
    >,
  };
};

const attrMeta = (
  attr: Omit<ZAttrMeta, "displayId" | "uid">,
  enabled?: boolean,
): ZMfNode => ({
  type: "attr",
  attr: {
    uid: "",
    displayId: 0,
    ...attr,
  },
  disableIf: enabled ? undefined : { type: "true" },
});

const optListFromValue = (value: ZSidLabel | null | undefined): ZSidLabel[] =>
  value ? [value] : [];
